import * as React from "react"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  formatPalletExchangeValue,
  formatTemperatureRange,
} from "@/lib/orderCargoFormat"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MapPin, Pencil, ReceiptText, Trash2 } from "lucide-react"
import { format } from "date-fns"
import { useDrag, useDragLayer, useDrop } from "react-dnd"
import { getEmptyImage } from "react-dnd-html5-backend"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "../ui/spinner"
import { useLocalStorage } from "usehooks-ts"
import { RouteStop } from "@/reducers/routes2gSlice"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import { AutocompleteGooglePlaces } from "@/components/maps/AutocompleteGooglePlaces"
import { Switch } from "@/components/ui/switch"
import { GeocodeStopInput, NaviSelectionInput } from "@/api/adHocCommands"
import { GeocodedStop, parseGeocodeStops } from "@/lib/geocodeStopsParser"
import { CopyCoordinatesButton } from "@/components/maps/CopyCoordinatesButton"
import { StopCoordinatesEditor } from "@/components/maps/StopCoordinatesEditor"
import NaviOrderPanel from "@/components/maps/NaviOrderPanel"
import AdHocCommands from "@/api/adHocCommands"
import { parseNaviJson } from "@/lib/naviParser"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromUser } from "@/jidUtils"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useAnalyzeOrderContextOptional } from "@/contexts/AnalyzeOrderContext"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

// The input a click opens with the cursor in it.
type StopFocusField =
  | "company_name"
  | "address"
  | "reference_number"
  | "scheduled_time"
  | "pallet_count"
  | "stop_weight"
type OrderFocusField =
  | "order_number"
  | "weight"
  | "price_netto_eur"
  | "notes"
  | "adr_info"
  | "pallet_exchange"
  | "temperature_min"
  | "temperature_max"

// Shared stop card content component (only content, no header)
function StopCardContent({
  stop,
  index,
  onEdit,
  onCoordinatesChange,
  showActions = false,
  disabled = false,
}: {
  stop: StopMetaData
  index: number
  onEdit?: (index: number, fieldToFocus?: StopFocusField) => void
  onCoordinatesChange?: (index: number, lat: number, lon: number) => void
  showActions?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const showFixedTimes = Boolean(useFeatureFlagEnabled("fixed-stop-times"))
  const [editingCoordinates, setEditingCoordinates] = useState(false)
  const canEditCoordinates =
    showActions && !!onCoordinatesChange && !disabled && !!stop.geocoded

  const scheduledDateText = (date: string) => {
    switch (stop.action) {
      case "pickup":
        return t("orders.verifyStops.stop.pickupDate", { date })
      case "delivery":
        return t("orders.verifyStops.stop.deliveryDate", { date })
      case "customs":
        return t("orders.verifyStops.stop.customsDate", { date })
      case "drop-off":
        return t("orders.verifyStops.stop.dropOffDate", { date })
      case "scale":
        return t("orders.verifyStops.stop.scaleDate", { date })
      default:
        return date
    }
  }

  return (
    <CardContent className="space-y-2 p-4 pt-0">
      <div
        className={cn(
          "select-text text-sm font-medium",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit && !disabled
            ? () => onEdit(index, "company_name")
            : undefined
        }
      >
        {stop.location?.companyName && stop.location?.companyName.length > 0 ? (
          stop.location.companyName
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addCompanyName")}
          </span>
        )}
      </div>
      <div
        className={cn(
          "select-text text-sm",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit && !disabled
            ? () => onEdit(index, "address")
            : undefined
        }
      >
        {stop.place ? (
          stop.place.formattedAddress
        ) : !stop.location.formattedAddress ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addAddress")}
          </span>
        ) : (
          `${stop.location.formattedAddress}`
        )}
      </div>
      {stop.geocoded && stop.geocoded.status !== "unavailable" && (
        <div
          data-testid="stop-coordinates"
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {editingCoordinates && onCoordinatesChange ? (
            <StopCoordinatesEditor
              lat={stop.geocoded.lat}
              lon={stop.geocoded.lon}
              onSave={({ lat, lon }) => {
                setEditingCoordinates(false)
                onCoordinatesChange(index, lat, lon)
              }}
              onCancel={() => setEditingCoordinates(false)}
            />
          ) : stop.geocoded.status === "pending" ? (
            <>
              <Spinner size="sm" />
              {t("orders.verifyStops.stop.locating")}
            </>
          ) : stop.geocoded.status === "found" ? (
            <>
              <MapPin className="h-3 w-3 shrink-0" />
              <span
                className="select-text"
                title={
                  stop.geocoded.source === "manual"
                    ? t("orders.verifyStops.stop.enteredByHand")
                    : undefined
                }
              >
                {formatCoordinates(stop.geocoded.lat, stop.geocoded.lon)}
              </span>
              {stop.geocoded.lat !== undefined &&
                stop.geocoded.lon !== undefined && (
                  <CopyCoordinatesButton
                    lat={stop.geocoded.lat}
                    lon={stop.geocoded.lon}
                  />
                )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
              {t("orders.verifyStops.stop.coordinatesNotFound")}
            </span>
          )}
          {!editingCoordinates &&
            canEditCoordinates &&
            stop.geocoded.status !== "pending" && (
              <button
                type="button"
                aria-label={t("orders.verifyStops.stop.editCoordinates")}
                title={t("orders.verifyStops.stop.editCoordinates")}
                data-testid="edit-coordinates"
                onClick={(event) => {
                  event.stopPropagation()
                  setEditingCoordinates(true)
                }}
                onMouseDown={(event) => event.stopPropagation()}
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
        </div>
      )}
      <div
        className={cn(
          "select-text text-sm",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit
            ? () => onEdit(index, "reference_number")
            : undefined
        }
      >
        {stop.action_reference_number ? (
          t("orders.verifyStops.stop.referenceNumber", {
            reference: stop.action_reference_number,
          })
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addReferenceNumber")}
          </span>
        )}
      </div>
      <div
        className={cn(
          "select-text text-sm",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit
            ? () => onEdit(index, "scheduled_time")
            : undefined
        }
      >
        {stop.scheduled_arrival_time ? (
          scheduledDateText(
            format(new Date(stop.scheduled_arrival_time), "dd-MM-yyyy", {
              locale: getDateFnsLocale(),
            }),
          )
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addScheduledTime")}
          </span>
        )}{" "}
        {stop.scheduled_arrival_time
          ? stop.scheduled_hour_min && stop.scheduled_hour_max
            ? `${stop.scheduled_hour_min.substring(0, 5)} - ${stop.scheduled_hour_max.substring(0, 5)}`
            : stop.scheduled_hour_min
              ? t("orders.verifyStops.stop.after", {
                  time: stop.scheduled_hour_min.substring(0, 5),
                })
              : stop.scheduled_hour_max
                ? t("orders.verifyStops.stop.before", {
                    time: stop.scheduled_hour_max.substring(0, 5),
                  })
                : ""
          : ""}
        {showFixedTimes && stop.time_fixed && stop.scheduled_arrival_time ? (
          <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
            {t("orders.verifyStops.stop.fixed")}
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          "select-text text-sm",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit && !disabled
            ? () => onEdit(index, "pallet_count")
            : undefined
        }
      >
        {stop.pallet_count ? (
          t("orders.verifyStops.stop.pallets", { value: stop.pallet_count })
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addPallets")}
          </span>
        )}
      </div>
      <div
        className={cn(
          "select-text text-sm",
          showActions &&
            onEdit &&
            !disabled &&
            "cursor-text rounded hover:bg-gray-50 dark:hover:bg-gray-800",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={
          showActions && onEdit && !disabled
            ? () => onEdit(index, "stop_weight")
            : undefined
        }
      >
        {stop.weight_kg ? (
          showActions ? (
            t("orders.verifyStops.stop.weight", { weight: stop.weight_kg })
          ) : (
            `${stop.weight_kg} kg`
          )
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300">
            {t("orders.verifyStops.stop.addWeight")}
          </span>
        )}
      </div>
    </CardContent>
  )
}

// Custom drag layer component
function CustomDragLayer({
  stops,
  getStepTitle,
  cardWidth,
}: {
  stops: StopMetaData[]
  getStepTitle: (action: string, index: number) => string
  formatDate: (dateStr: string, timeStr: string) => string
  cardWidth: number
}) {
  const {
    isDragging,
    item,
    currentOffset,
    initialOffset,
    initialSourceClientOffset,
  } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
    initialOffset: monitor.getInitialClientOffset(),
    initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
  }))

  if (
    !isDragging ||
    !currentOffset ||
    !initialOffset ||
    !initialSourceClientOffset ||
    !item
  ) {
    return null
  }

  const stop = stops[item.index]
  if (!stop) return null

  const offsetY = initialSourceClientOffset.y - initialOffset.y

  // Keep card at its original x position, only allow vertical movement
  const cardX = initialSourceClientOffset.x
  const cardY = currentOffset.y + offsetY

  return (
    <div
      className="pointer-events-none fixed left-0 top-0 z-[1001]"
      style={{
        transform: `translate(${cardX}px, ${cardY}px)`,
      }}
    >
      <div style={{ width: cardWidth || 384 }}>
        <Card className="shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between p-4 pb-1 pt-2">
            <h3 className="text-lg font-semibold">
              {getStepTitle(stop.action, item.index)}
            </h3>
          </CardHeader>
          <StopCardContent
            stop={stop}
            index={item.index}
            showActions={false}
            disabled={false}
          />
        </Card>
      </div>
    </div>
  )
}

// DraggableStopCard component
type DraggableStopCardProps = {
  index: number
  stop: StopMetaData
  editingIndex: number | null
  isEditingNotes: boolean
  onEdit: (index: number, fieldToFocus?: StopFocusField) => void
  onDelete: (index: number) => void
  onMove: (dragIndex: number, hoverIndex: number) => void
  getStepTitle: (action: string, index: number) => string
  formatDate: (dateStr: string, timeStr: string) => string
  measureRef?: React.RefObject<HTMLDivElement | null>
  isFetchingPolylines: boolean
  onCoordinatesChange?: (index: number, lat: number, lon: number) => void
}

function DraggableStopCard({
  index,
  stop,
  editingIndex,
  isEditingNotes,
  onEdit,
  onDelete,
  onMove,
  getStepTitle,
  measureRef,
  isFetchingPolylines,
  onCoordinatesChange,
}: DraggableStopCardProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const ItemType = "STOP"

  const [, drop] = useDrop({
    accept: ItemType,
    hover(item: { index: number; originalIndex: number }, monitor) {
      if (!ref.current) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      // Get rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Get mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = clientOffset?.y
        ? clientOffset.y - hoverBoundingRect.top
        : 0

      // Only perform the move when the mouse has crossed half of the item's height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const [, drag, preview] = useDrag({
    type: ItemType,
    item: { index, originalIndex: index },
    canDrag: () => editingIndex === null && !isEditingNotes,
  })

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  drag(drop(ref))

  // Combine refs for measurement and drag/drop
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node
      if (measureRef && node) {
        ;(measureRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node
      }
    },
    [measureRef],
  )

  return (
    <Card
      ref={combinedRef}
      className={cn(
        "group mb-4 shadow-sm",
        (isEditingNotes || (editingIndex !== null && editingIndex !== index)) &&
          "opacity-50",
        editingIndex === null && !isEditingNotes && "cursor-move",
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between p-4 pb-1 pt-2">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">
            {getStepTitle(stop.action, index)}
          </h3>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            disabled={editingIndex !== null}
            size="sm"
            onClick={() => onEdit(index)}
            className={cn(
              "h-8 w-8 p-0",
              editingIndex !== null || isEditingNotes
                ? "invisible"
                : "invisible group-hover:visible",
            )}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            disabled={editingIndex !== null}
            size="sm"
            onClick={() => onDelete(index)}
            className={cn(
              "h-8 w-8 p-0 text-red-500 hover:text-red-700",
              editingIndex !== null || isEditingNotes
                ? "invisible"
                : "invisible group-hover:visible",
            )}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <StopCardContent
        stop={stop}
        index={index}
        onEdit={onEdit}
        onCoordinatesChange={onCoordinatesChange}
        showActions={true}
        disabled={isFetchingPolylines}
      />
    </Card>
  )
}

export type StopMetaData = {
  action: string
  scheduled_arrival_time: string | undefined
  scheduled_hour_min: string | undefined
  scheduled_hour_max: string | undefined
  time_fixed?: boolean
  location: {
    formattedAddress: string
    companyName: string
    postalCode?: string
    street?: string
    locality?: string
    country?: string
    countryCode?: string
    lat?: string
    lon?: string
  }
  place: google.maps.places.Place | undefined
  action_reference_number: string
  pallet_count: string
  weight_kg: string
  // Coordinates gps will assign to this stop when the route is generated,
  // previewed via geocode_stops. Keyed by the address it was computed for.
  geocoded?: StopGeocodeState
}

export type StopGeocodeState = {
  status: "pending" | "found" | "not_found" | "unavailable"
  signature: string
  lat?: number
  lon?: number
  formattedAddress?: string
  country?: string
  countryCode?: string
  // "manual": the dispatcher typed the pair in; it is the point, not a preview
  // of one, so gps is not asked again for it.
  source?: "gps" | "manual"
}

// A hand-entered pair still stands as long as the stop carries exactly it: an
// address edit that brings new coordinates from Places replaces it.
export const manualCoordinatesIntact = (stop: StopMetaData): boolean =>
  stop.geocoded?.source === "manual" &&
  stop.geocoded.status === "found" &&
  stop.geocoded.lat !== undefined &&
  stop.geocoded.lon !== undefined &&
  stop.location?.lat === String(stop.geocoded.lat) &&
  stop.location?.lon === String(stop.geocoded.lon)

// The dispatcher's pair becomes the stop's coordinates: plan_route gets it as
// "lat,lon", Marcos gets it verbatim, and the card shows it instead of a
// gps preview.
export const withManualCoordinates = (
  stop: StopMetaData,
  lat: number,
  lon: number,
): StopMetaData => {
  const next: StopMetaData = {
    ...stop,
    location: { ...stop.location, lat: String(lat), lon: String(lon) },
  }
  return {
    ...next,
    geocoded: {
      status: "found",
      signature: geocodeSignature(next),
      lat,
      lon,
      source: "manual",
    },
  }
}

// Which vhost's gps to ask for the preview: Marcos flows always ask (the
// order is created from this step), other accounts only when the
// stop-coordinates-preview flag is on for them; the vhost then comes from
// the signed-in user's JID.
export const coordinatePreviewVhost = ({
  naviVhost,
  flagEnabled,
  userJid,
}: {
  naviVhost?: string
  flagEnabled: boolean
  userJid?: string
}): string | undefined => {
  if (naviVhost) return naviVhost
  if (!flagEnabled || !userJid) return undefined
  try {
    return extractVhostFromUser(userJid) || undefined
  } catch {
    return undefined
  }
}

// The address fields plan_route geocodes for a stop, exactly as buildStops
// sends them with useLatLon: a stop that already has coordinates (a GPS pair
// printed on the document) goes out as "lat,lon", everything else as the
// address text. Same input, same geocoder call, so the preview is the point
// the route will be planned on.
export const geocodeInputFor = (stop: StopMetaData): GeocodeStopInput => ({
  id: "",
  address:
    stop.location?.lat && stop.location?.lon
      ? `${stop.location.lat},${stop.location.lon}`
      : stop.place?.formattedAddress || stop.location?.formattedAddress || "",
  companyName: stop.location?.companyName || "",
  postalCode:
    (stop.place?.addressComponents
      ? stop.place.addressComponents.find((c) =>
          c.types.includes("postal_code"),
        )?.longText
      : undefined) ??
    stop.location?.postalCode ??
    "",
})

export const geocodeSignature = (stop: StopMetaData): string => {
  const input = geocodeInputFor(stop)
  return [input.address, input.companyName, input.postalCode].join("\u0000")
}

export const formatCoordinates = (lat?: number, lon?: number): string =>
  lat === undefined || lon === undefined
    ? ""
    : `${lat.toFixed(5)}, ${lon.toFixed(5)}`

// Stops that still need a preview: nothing computed yet (or computed for a
// different address) and an address to geocode.
export const stopsNeedingGeocode = (stops: StopMetaData[]): StopMetaData[] =>
  stops.filter(
    (stop) =>
      geocodeInputFor(stop).address.trim() !== "" &&
      !manualCoordinatesIntact(stop) &&
      (!stop.geocoded || stop.geocoded.signature !== geocodeSignature(stop)),
  )

export const markGeocodePending = (
  stops: StopMetaData[],
  signatures: Set<string>,
): StopMetaData[] =>
  stops.map((stop) => {
    const signature = geocodeSignature(stop)
    if (!signatures.has(signature)) return stop
    if (stop.geocoded?.signature === signature) return stop
    return { ...stop, geocoded: { status: "pending", signature } }
  })

// Applies a geocode_stops reply. Results are matched by the signature they
// were requested for, so a stop edited while the request was in flight keeps
// waiting for its own answer instead of taking a stale one.
export const applyGeocodeResults = (
  stops: StopMetaData[],
  results: Map<string, GeocodedStop | "unavailable">,
): StopMetaData[] =>
  stops.map((stop) => {
    const signature = geocodeSignature(stop)
    const result = results.get(signature)
    if (result === undefined) return stop
    if (result === "unavailable") {
      return { ...stop, geocoded: { status: "unavailable", signature } }
    }
    return {
      ...stop,
      geocoded: result.found
        ? {
            status: "found",
            signature,
            lat: result.lat,
            lon: result.lon,
            formattedAddress: result.formattedAddress,
            country: result.country,
            countryCode: result.countryCode,
            source: "gps",
          }
        : { status: "not_found", signature },
    }
  })

// True while gps still owes a preview for some stop: either the request is in
// flight or the stop's address changed and the effect has not re-asked yet.
export const geocodeInFlight = (stops: StopMetaData[]): boolean =>
  stops.some(
    (stop) =>
      stop.geocoded?.status === "pending" ||
      stopsNeedingGeocode([stop]).length > 0,
  )

// Stops as sent to Marcos: the point gps previewed replaces whatever the stop
// carried, so the order holds the same point the route is planned on. The
// country comes along when the document did not state it, since Marcos wants
// a country on every station.
export const withGeocodedCoordinates = (
  stops: StopMetaData[],
): StopMetaData[] =>
  stops.map((stop) => {
    const geocoded = stop.geocoded
    if (
      !geocoded ||
      geocoded.status !== "found" ||
      (geocoded.signature !== geocodeSignature(stop) &&
        !manualCoordinatesIntact(stop)) ||
      geocoded.lat === undefined ||
      geocoded.lon === undefined
    ) {
      return stop
    }
    return {
      ...stop,
      location: {
        ...stop.location,
        lat: String(geocoded.lat),
        lon: String(geocoded.lon),
        country: stop.location?.country || geocoded.country,
        countryCode: stop.location?.countryCode || geocoded.countryCode,
      },
    }
  })

export type BillingMetaData = {
  original_transport_documents_required?: boolean
  original_transport_documents_notes?: string
  digital_transport_documents_notes?: string
  payment_terms_days?: number
  payment_terms_notes?: string
  client_platform_upload_required?: boolean
  client_platform_upload_notes?: string
  important_settlement_notes?: string
}

export type OrderMetaData = {
  stops?: StopMetaData[]
  ref_number: string
  notes: string
  pallet_exchange_notes?: string
  // Yes/no behind pallet_exchange_notes; undefined when the order says nothing.
  pallet_exchange?: boolean
  // Required cargo temperature range in °C; a single set point fills both.
  temperature_min?: number
  temperature_max?: number
  adr_info?: string
  weight: string
  price_netto_eur?: number
  billing?: BillingMetaData
  nip?: string
  order_number?: string
  transport_number?: string
  payment_deadline?: string
  customer_name?: string
  customer_address?: string
  customer_postal_code?: string
  customer_contact?: string
  customer_email?: string
  customer_phone?: string
  // Recipient (RECIPIENT/ODBIORCA) — the carrier the order is addressed to.
  recipient_name?: string
  // Recipient/carrier's NIP, tried before recipient_name (see NaviOrderPanel's
  // autoSelectNip for the Przewoźnik dropdown).
  recipient_nip?: string
  dispatch_location?: string
  driver_name?: string
  car_plates?: string
  trailer_plates?: string
  coordinator_name?: string
  empty_kilometers?: string
  loaded_kilometers?: string
}

const hasTextValue = (value?: string | null): value is string =>
  Boolean(value && value.trim().length > 0)

export const deriveIsAdr = (orderData?: OrderMetaData): boolean =>
  Boolean(orderData?.adr_info?.trim())

const parseTemperatureInput = (text: string): number | undefined => {
  const value = text.trim().replace(",", ".")
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const hasBillingData = (billing?: BillingMetaData) =>
  Boolean(
    billing &&
      (billing.original_transport_documents_required !== undefined ||
        hasTextValue(billing.original_transport_documents_notes) ||
        hasTextValue(billing.digital_transport_documents_notes) ||
        billing.payment_terms_days !== undefined ||
        hasTextValue(billing.payment_terms_notes) ||
        billing.client_platform_upload_required !== undefined ||
        hasTextValue(billing.client_platform_upload_notes) ||
        hasTextValue(billing.important_settlement_notes)),
  )

const formatBillingRequirement = (t: TFunction, value?: boolean) => {
  if (value === true) return t("orders.verifyStops.billing.required")
  if (value === false) return t("orders.verifyStops.billing.notRequired")
  return undefined
}

type BillingRow = {
  label: string
  value?: string
  tone?: "positive" | "neutral" | "info"
}

const hasBillingRowValue = (
  row: BillingRow,
): row is BillingRow & { value: string } => hasTextValue(row.value)

function BillingDetailsSection({
  billing,
  priceNettoEur,
}: {
  billing?: BillingMetaData
  priceNettoEur?: number
}) {
  const { t } = useTranslation()
  if (!hasBillingData(billing) && priceNettoEur === undefined) return null

  const billingRowsBase: BillingRow[] = [
    {
      label: t("orders.verifyStops.billing.originalDocuments"),
      value: formatBillingRequirement(
        t,
        billing?.original_transport_documents_required,
      ),
      tone: billing?.original_transport_documents_required
        ? "positive"
        : "neutral",
    },
    {
      label: t("orders.verifyStops.billing.originalDocumentsDetails"),
      value: billing?.original_transport_documents_notes,
    },
    {
      label: t("orders.verifyStops.billing.digitalDocumentsDetails"),
      value: billing?.digital_transport_documents_notes,
    },
    {
      label: t("orders.verifyStops.billing.price"),
      value: priceNettoEur !== undefined ? `${priceNettoEur} EUR` : undefined,
      tone: "info",
    },
    {
      label: t("orders.verifyStops.billing.paymentTerms"),
      value:
        billing?.payment_terms_days !== undefined
          ? t("orders.verifyStops.billing.paymentTermsDays", {
              count: billing.payment_terms_days,
            })
          : undefined,
      tone: "info",
    },
    {
      label: t("orders.verifyStops.billing.paymentTermsDetails"),
      value: billing?.payment_terms_notes,
    },
    {
      label: t("orders.verifyStops.billing.platformUpload"),
      value: formatBillingRequirement(
        t,
        billing?.client_platform_upload_required,
      ),
      tone: billing?.client_platform_upload_required ? "positive" : "neutral",
    },
    {
      label: t("orders.verifyStops.billing.platformUploadDetails"),
      value: billing?.client_platform_upload_notes,
    },
    {
      label: t("orders.verifyStops.billing.settlementNotes"),
      value: billing?.important_settlement_notes,
    },
  ]

  const billingRows = billingRowsBase.filter(hasBillingRowValue)

  if (billingRows.length === 0) return null

  const toneClasses: Record<NonNullable<BillingRow["tone"]>, string> = {
    positive: "text-emerald-700 dark:text-emerald-400",
    neutral: "text-slate-600 dark:text-slate-400",
    info: "text-sky-700 dark:text-sky-400",
  }

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="mb-3 flex items-center gap-2">
        <ReceiptText className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {t("orders.verifyStops.billing.title")}
        </p>
      </div>
      <dl className="divide-y divide-slate-100 dark:divide-slate-800/80">
        {billingRows.map((row) => (
          <div
            key={row.label}
            className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
          >
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400 sm:w-52 sm:shrink-0 sm:pt-0.5">
              {row.label}
            </dt>
            <dd
              className={cn(
                "select-text whitespace-pre-wrap text-sm leading-6 text-slate-800 dark:text-slate-200 sm:flex-1 sm:text-right",
                row.tone && "font-medium",
                row.tone && toneClasses[row.tone],
              )}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

const buildVerifyDataPayload = (
  messageId: string,
  metaData: OrderMetaData,
) => ({
  messageId,
  stops: metaData.stops?.map((stop) => ({
    action: stop.action,
    companyName: stop.location?.companyName,
    address: stop.place?.formattedAddress || stop.location?.formattedAddress,
    referenceNumber: stop.action_reference_number,
    scheduledArrivalTime: stop.scheduled_arrival_time,
    scheduledHourMin: stop.scheduled_hour_min,
    scheduledHourMax: stop.scheduled_hour_max,
    timeFixed: stop.time_fixed,
    palletCount: stop.pallet_count,
    weightKg: stop.weight_kg,
  })),
  refNumber: metaData.order_number || "",
  weight: metaData.weight,
  notes: metaData.notes,
  priceNettoEur: metaData.price_netto_eur,
  billing: metaData.billing,
})

type AnalyzeOrderVerifyStopsProps = {
  data: OrderMetaData
  onContinue: (metaData: OrderMetaData, shouldFetch?: boolean) => void
  onCloseFullScreen: () => void
  isFetchingPolylines: boolean
  isRouteFetched: boolean
  onEdited: () => void
  onResetMessageGenerated: () => void
  extractedStops: RouteStop[]
  loadedKm?: number
  showSkipStepButton?: boolean
  messageId?: string | null
  // Overrides the "Generate route"/"Continue" CTA label (e.g. the planner
  // saves to the unassigned pool instead of generating a route).
  continueLabel?: string
  navi?: {
    vhost: string
    driverJid: string
    driverPhone?: string
    // Fallback for the Kierowca auto-select when Marcos has no phone number
    // on file for this driver (phone matching then finds nothing to match).
    driverFirstName?: string
    driverLastName?: string
    truckPlate?: string
    trailerPlate?: string
    dispatcherFirstName?: string
    dispatcherLastName?: string
    // Order document to attach to the created Marcos transport. The chat flow
    // reads it from AnalyzeOrderContext; the on-page flow passes it here.
    uploadedFile?: { url?: string; name?: string }
    setNaviTransportId: (id: number | undefined) => void
    setNaviSelection: (s: NaviSelectionInput | undefined) => void
    // Overlay control for the whole left panel: fired when the "Generate route"
    // submit begins creating the Marcos order, and when it fails (so the parent
    // can hide the overlay). On success the overlay stays until this step
    // unmounts (we move on to the map).
    onSubmitStart?: () => void
    onSubmitError?: () => void
  }
}

export function AnalyzeOrderVerifyStops({
  data,
  onContinue,
  onCloseFullScreen,
  isFetchingPolylines,
  isRouteFetched,
  onEdited,
  onResetMessageGenerated,
  extractedStops,
  loadedKm,
  showSkipStepButton = true,
  messageId,
  navi,
  continueLabel,
}: AnalyzeOrderVerifyStopsProps) {
  const { t } = useTranslation()
  const [shouldSkipVerifyStep, setShouldSkipVerifyStep] = useLocalStorage(
    "shouldSkipVerifyStep",
    false,
  )
  // With the Marcos panel this step creates the order, so skipping it is not
  // offered (AnalyzeOrderContext never skips it for Marcos either).
  const offerSkipStep = showSkipStepButton && !navi

  const showFixedTimes = Boolean(useFeatureFlagEnabled("fixed-stop-times"))

  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)

  const { client, myJid } = useContext(XmppContext)
  const previewFlag = Boolean(useFeatureFlagEnabled("stop-coordinates-preview"))
  const analyzeOrderContext = useAnalyzeOrderContextOptional()
  const firstUploadedFile =
    analyzeOrderContext?.files?.[0] ?? navi?.uploadedFile
  const [naviComplete, setNaviComplete] = useState(false)
  const [naviSelection, setNaviSelectionState] = useState<
    NaviSelectionInput | undefined
  >(undefined)
  const [naviError, setNaviError] = useState<string | undefined>(undefined)
  const [naviCreating, setNaviCreating] = useState(false)

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editData, setEditData] = useState<StopMetaData | null>(null)
  const [stops, setStops] = useState(
    extractedStops.filter((stop) => !stop.id) as unknown as StopMetaData[],
  )
  const [cardWidth, setCardWidth] = useState<number>(0)
  const cardMeasureRef = useRef<HTMLDivElement | null>(null)

  // Preview the coordinates gps will assign to each stop: always when Marcos
  // is configured (that flow creates the order before the route exists, so
  // this is where the dispatcher sees the point and where Marcos gets it
  // from), and for any vhost on the stop-coordinates-preview flag, so the
  // dispatcher can check and correct the point before it goes to the driver.
  const previewVhost = coordinatePreviewVhost({
    naviVhost: navi?.vhost,
    flagEnabled: previewFlag,
    userJid: myJid?.toString(),
  })
  useEffect(() => {
    if (!previewVhost || !client) return
    const pending = stopsNeedingGeocode(stops)
    if (pending.length === 0) return
    const signatures = Array.from(new Set(pending.map(geocodeSignature)))
    const bySignature = new Map(
      pending.map((stop) => [geocodeSignature(stop), geocodeInputFor(stop)]),
    )
    const inputs = signatures.map((signature, index) => ({
      ...bySignature.get(signature)!,
      id: String(index),
    }))
    setStops((prev) => markGeocodePending(prev, new Set(signatures)))
    AdHocCommands.geocodeStops(client, previewVhost, inputs)
      .then((reply) => {
        const parsed = parseGeocodeStops(reply)
        const results = new Map<string, GeocodedStop | "unavailable">()
        signatures.forEach((signature, index) => {
          const match =
            parsed.find((r) => r.id === String(index)) ?? parsed[index]
          results.set(signature, match ?? { id: String(index), found: false })
        })
        setStops((prev) => applyGeocodeResults(prev, results))
      })
      .catch((error) => {
        // Older gps without geocode_stops, or a transport error: hide the
        // line rather than claim the address is unresolvable.
        console.warn("geocode_stops failed", error)
        const results = new Map<string, GeocodedStop | "unavailable">(
          signatures.map((signature) => [signature, "unavailable"]),
        )
        setStops((prev) => applyGeocodeResults(prev, results))
      })
  }, [stops, previewVhost, client])
  const [focusField, setFocusField] = useState<
    StopFocusField | OrderFocusField | null
  >(null)

  const [isNewStop, setIsNewStop] = useState<boolean>(false)
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false)
  const [orderData, setOrderData] = useState<OrderMetaData>(data)
  const [currentOrderData, setCurrentOrderData] = useState<OrderMetaData>(data)
  // The number that names this order, wherever it currently lives.
  const orderNumber = orderData.order_number || ""
  const [showOrderDetails, setShowOrderDetails] = useState<boolean>(
    () =>
      !!(
        data.order_number ||
        data.weight ||
        data.notes ||
        data.price_netto_eur ||
        hasBillingData(data.billing)
      ),
  )
  const [datePickerOpened, setDatePickerOpened] = useState<boolean>(false)
  const trackedPresentedMessageId = useRef<string | null>(null)

  // Track initial data when component loads
  useEffect(() => {
    if (messageId && trackedPresentedMessageId.current !== messageId) {
      trackedPresentedMessageId.current = messageId
      window.analytics.track(
        "AnalyzeOrderVerifyDataPresented",
        buildVerifyDataPayload(messageId, { ...data, stops }),
      )
    }
  }, [messageId, stops, data])

  useEffect(() => {
    if (cardMeasureRef.current) {
      setCardWidth(cardMeasureRef.current.offsetWidth)
    }
  }, [])

  // Wrapper that tracks data before calling onContinue
  const handleContinueWithTracking = useCallback(
    (metaData: OrderMetaData, shouldFetch?: boolean) => {
      if (messageId) {
        window.analytics.track(
          "AnalyzeOrderVerifyDataSubmitted",
          buildVerifyDataPayload(messageId, metaData),
        )
      }
      onContinue(metaData, shouldFetch)
    },
    [messageId, onContinue],
  )

  const handleEdit = (index: number, fieldToFocus?: StopFocusField) => {
    if (!editingIndex && !isEditingNotes && !isFetchingPolylines) {
      setEditingIndex(index)
      setEditData({ ...(stops[index] as StopMetaData) })
      setIsNewStop(false)
      setFocusField(fieldToFocus || null)
      setDatePickerOpened(false) // Reset when starting to edit
    }
  }

  const handleEditOrderField = (fieldToFocus: OrderFocusField) => {
    if (!editingIndex && !isEditingNotes && !isFetchingPolylines) {
      setIsEditingNotes(true)
      setCurrentOrderData(orderData)
      setFocusField(fieldToFocus)
    }
  }

  const handleCancel = useCallback(() => {
    if (isNewStop && editingIndex !== null) {
      // Remove the newly added stop if cancelling
      const newStops = stops.filter((_, i) => i !== editingIndex)
      setStops(newStops)
    }
    setEditingIndex(null)
    setEditData(null)
    setIsNewStop(false)
    setFocusField(null)
    setDatePickerOpened(false)
  }, [editingIndex, isNewStop, stops])

  const handleEditNotes = () => {
    setIsEditingNotes(true)
    setCurrentOrderData(orderData)
  }

  const handleCancelNotes = useCallback(() => {
    setIsEditingNotes(false)
    setOrderData(currentOrderData)
    setFocusField(null)
  }, [currentOrderData])

  const handleSaveNotes = (shouldContinue: boolean) => {
    onEdited()
    onResetMessageGenerated()
    setIsEditingNotes(false)
    setFocusField(null)
    if (shouldContinue) {
      handleContinueWithTracking(
        { ...orderData, stops: stops as StopMetaData[] },
        true,
      )
    }
  }

  const handleSave = (shouldContinue: boolean) => {
    if (editingIndex !== null && editData) {
      onEdited()
      onResetMessageGenerated()
      const newStops = [...stops] as StopMetaData[]
      newStops[editingIndex] = editData
      setStops(newStops)
      setEditingIndex(null)
      setEditData(null)
      setIsNewStop(false)
      setFocusField(null)
      setDatePickerOpened(false)
      if (shouldContinue) {
        handleContinueWithTracking({ ...orderData, stops: newStops }, true)
      }
    }
  }

  const handleDelete = (index: number) => {
    onEdited()
    onResetMessageGenerated()
    const newStops = stops.filter((_, i) => i !== index)
    setStops(newStops)
  }

  const handleCoordinatesChange = (index: number, lat: number, lon: number) => {
    onEdited()
    onResetMessageGenerated()
    setStops((prev) =>
      prev.map((stop, i) =>
        i === index ? withManualCoordinates(stop, lat, lon) : stop,
      ),
    )
  }

  const handleAddStop = () => {
    onEdited()
    onResetMessageGenerated()
    const newStop: StopMetaData = {
      action: "delivery",
      scheduled_arrival_time: undefined,
      scheduled_hour_min: "",
      scheduled_hour_max: "",
      time_fixed: false,
      location: {
        formattedAddress: "",
        companyName: "",
      },
      place: undefined,
      action_reference_number: "",
      pallet_count: "",
      weight_kg: "",
    }
    setStops([...stops, newStop])
    setEditingIndex(stops.length)
    setEditData(newStop)
    setIsNewStop(true) // Mark this as a newly added stop
  }

  const moveStop = (dragIndex: number, hoverIndex: number) => {
    onEdited()
    onResetMessageGenerated()
    const dragStop = stops[dragIndex]
    const newStops = [...stops]
    newStops.splice(dragIndex, 1)
    newStops.splice(hoverIndex, 0, dragStop)
    setStops(newStops)
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (editingIndex !== null) {
          handleCancel()
        } else if (isEditingNotes) {
          handleCancelNotes()
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [
    editingIndex,
    handleCancel,
    handleCancelNotes,
    isEditingNotes,
    onCloseFullScreen,
  ])

  const formatDate = (dateStr: string, timeStr: string) => {
    const date = new Date(dateStr)

    // Parse the timeStr to get a proper time object
    // Assuming timeStr is in format like "13:45:00" or "13:45"
    const timeParts = timeStr.split(":")
    const hours = timeParts[0]
    const minutes = timeParts[1]

    // Create a formatted time (HH:mm) and date (dd.MM.yyyy)
    const formattedTime = `${hours}:${minutes}`
    const formattedDate = format(date, "dd.MM.yyyy")

    return `${formattedTime} ${formattedDate}`
  }

  const getActionTypeCount = (stops: StopMetaData[], currentIndex: number) => {
    const currentAction = stops[currentIndex].action
    let count = 0

    for (let i = 0; i <= currentIndex; i++) {
      if (stops[i].action === currentAction) {
        count++
      }
    }

    return count
  }

  const getStepTitle = (action: string, index: number) => {
    const actionCount = getActionTypeCount(stops, index)
    if (action === "pickup")
      return t("orders.verifyStops.stepTitle.pickup", { number: actionCount })
    if (action === "delivery")
      return t("orders.verifyStops.stepTitle.delivery", { number: actionCount })
    if (action === "customs")
      return t("orders.verifyStops.stepTitle.customs", { number: actionCount })
    if (action === "drop-off")
      return t("orders.verifyStops.stepTitle.dropOff", { number: actionCount })
    if (action === "scale")
      return t("orders.verifyStops.stepTitle.scale", { number: actionCount })
    return `#${actionCount}`
  }

  const handleRemoveOrderDetails = () => {
    onEdited()
    setOrderData({
      ...orderData,
      order_number: "",
      weight: "",
      notes: "",
      adr_info: undefined,
      price_netto_eur: undefined,
      billing: undefined,
    })
    setShowOrderDetails(false)
  }

  const handleAddOrderDetails = () => {
    setShowOrderDetails(true)
    handleEditOrderField("order_number")
  }

  const renderEditCard = (stop: StopMetaData, index: number) => {
    return (
      <Card key={index} className="mb-4 shadow-sm">
        <CardHeader className="flex min-h-[50px] flex-row items-center justify-between p-4 pb-1 pt-2">
          <h3 className="text-lg font-semibold">
            {getStepTitle(stop.action, index)}
          </h3>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              onClick={() => handleSave(false)}
              className="h-6 bg-blue-600 hover:bg-blue-700 dark:text-white"
            >
              {t("common.save")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 pt-0">
          <div>
            <Label htmlFor="action" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.actionType")}
            </Label>
            <Select
              value={editData?.action || "delivery"}
              onValueChange={(value) =>
                setEditData((prev) =>
                  prev ? { ...prev, action: value } : null,
                )
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue
                  placeholder={t(
                    "orders.verifyStops.editStop.selectActionType",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pickup">
                  {t("orders.verifyStops.actions.pickup")}
                </SelectItem>
                <SelectItem value="delivery">
                  {t("orders.verifyStops.actions.delivery")}
                </SelectItem>
                <SelectItem value="customs">
                  {t("orders.verifyStops.actions.customs")}
                </SelectItem>
                <SelectItem value="drop-off">
                  {t("orders.verifyStops.actions.dropOff")}
                </SelectItem>
                <SelectItem value="scale">
                  {t("orders.verifyStops.actions.scale")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="company_name" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.companyName")}
            </Label>
            <Input
              id="company_name"
              value={editData?.location?.companyName || ""}
              onChange={(e) =>
                setEditData((prev) =>
                  prev
                    ? {
                        ...prev,
                        location: {
                          ...prev.location,
                          companyName: e.target.value,
                        },
                      }
                    : null,
                )
              }
              className="mt-1"
              autoFocus={focusField === "company_name"}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <div>
            <Label htmlFor="address" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.address")}
            </Label>
            {focusField === "address" ? (
              <AutocompleteGooglePlaces
                initialValue={
                  editData?.place?.formattedAddress ||
                  `${editData?.location.formattedAddress}` ||
                  ""
                }
                placeholder={t("orders.verifyStops.editStop.enterAddress")}
                onLocationSelect={(location) => {
                  setEditData((prev) =>
                    prev
                      ? {
                          ...prev,
                          location: {
                            ...prev.location,
                            ...location,
                            companyName:
                              location.companyName ?? prev.location.companyName,
                          },
                        }
                      : prev,
                  )
                }}
                onPlaceSelect={(place) => {
                  setEditData((prev) => (prev ? { ...prev, place } : prev))
                }}
                onAbort={() => {
                  setFocusField(null)
                }}
                autoFocus={true}
              />
            ) : (
              <div
                className="flex h-9 w-full cursor-pointer items-center rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => setFocusField("address")}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    setFocusField("address")
                  }
                }}
              >
                {editData?.place?.formattedAddress ||
                editData?.location.formattedAddress ? (
                  <span className="text-foreground">
                    {editData?.place?.formattedAddress ||
                      editData?.location.formattedAddress}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {t("orders.verifyStops.editStop.clickToEnterAddress")}
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="reference_number" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.referenceNumber")}
            </Label>
            <Input
              id="reference_number"
              value={editData?.action_reference_number || ""}
              onChange={(e) =>
                setEditData((prev) =>
                  prev
                    ? { ...prev, action_reference_number: e.target.value }
                    : null,
                )
              }
              className="mt-1"
              autoFocus={focusField === "reference_number"}
              onFocus={(e) => e.target.select()}
            />
          </div>

          <div>
            <Label htmlFor="date" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.dateAndTime")}
            </Label>
            <div className="flex items-center gap-2">
              <DateTimePicker
                modal={true}
                hideTime
                value={
                  editData?.scheduled_arrival_time
                    ? new Date(`${editData.scheduled_arrival_time}`)
                    : undefined
                }
                onChange={(selectedDate) => {
                  if (editData && selectedDate) {
                    setEditData({
                      ...editData,
                      scheduled_arrival_time: format(
                        selectedDate,
                        "yyyy-MM-dd'T'HH:mm:ss'",
                      ),
                    })
                  }
                }}
                renderTrigger={({ value, setOpen }) => {
                  // Auto-open effect when this field should be focused
                  useEffect(() => {
                    if (focusField === "scheduled_time" && !datePickerOpened) {
                      setOpen(true)
                      setDatePickerOpened(true)
                    }
                  }, [setOpen])

                  return (
                    <div
                      className="flex h-9 w-full cursor-pointer items-center rounded-md border border-input px-3 py-2 text-sm shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => setOpen(true)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          setOpen(true)
                        }
                      }}
                    >
                      {value
                        ? format(value, "dd-MM-yyyy", {
                            locale: getDateFnsLocale(),
                          })
                        : t("orders.verifyStops.editStop.pickDate")}
                    </div>
                  )
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (editData) {
                    setEditData({
                      ...editData,
                      scheduled_arrival_time: undefined,
                    })
                  }
                }}
                className="h-8 w-8"
                aria-label={t("orders.verifyStops.editStop.removeDate")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
              {t("orders.verifyStops.editStop.min")}
              <Input
                type="time"
                id="time-picker"
                step="60"
                value={editData?.scheduled_hour_min}
                onChange={(event) => {
                  if (editData) {
                    setEditData({
                      ...editData,
                      scheduled_hour_min: event.target.value,
                    })
                  }
                }}
                className="max-w-[80px] appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (editData) {
                    setEditData({
                      ...editData,
                      scheduled_hour_min: "",
                    })
                  }
                }}
                className="h-8 w-8"
                aria-label={t("orders.verifyStops.editStop.removeMinTime")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
              {t("orders.verifyStops.editStop.max")}
              <Input
                type="time"
                id="time-picker"
                step="60"
                value={editData?.scheduled_hour_max}
                onChange={(event) => {
                  if (editData) {
                    setEditData({
                      ...editData,
                      scheduled_hour_max: event.target.value,
                    })
                  }
                }}
                className="max-w-[80px] appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (editData) {
                    setEditData({
                      ...editData,
                      scheduled_hour_max: "",
                    })
                  }
                }}
                className="h-8 w-8"
                aria-label={t("orders.verifyStops.editStop.removeMaxTime")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
              {showFixedTimes && (
                <label className="flex cursor-pointer items-center gap-1.5 text-sm font-medium">
                  <Checkbox
                    checked={Boolean(editData?.time_fixed)}
                    onCheckedChange={(checked) => {
                      if (editData) {
                        setEditData({
                          ...editData,
                          time_fixed: checked === true,
                        })
                      }
                    }}
                  />
                  {t("orders.verifyStops.stop.fixed")}
                </label>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="pallet_count" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.pallets")}
            </Label>
            <Input
              id="pallet_count"
              type="text"
              value={editData?.pallet_count || ""}
              onChange={(e) =>
                setEditData((prev) =>
                  prev
                    ? {
                        ...prev,
                        pallet_count: e.target.value,
                      }
                    : null,
                )
              }
              className="mt-1"
              autoFocus={focusField === "pallet_count"}
              onFocus={(e) => e.target.select()}
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="stop_weight" className="text-sm font-medium">
              {t("orders.verifyStops.editStop.weight")}
            </Label>
            <Input
              id="stop_weight"
              type="number"
              value={editData?.weight_kg || ""}
              onChange={(e) =>
                setEditData((prev) =>
                  prev ? { ...prev, weight_kg: e.target.value } : null,
                )
              }
              className="mt-1"
              autoFocus={focusField === "stop_weight"}
              onFocus={(e) => e.target.select()}
              min="0"
              step="0.1"
            />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <CustomDragLayer
        stops={stops}
        getStepTitle={getStepTitle}
        formatDate={formatDate}
        cardWidth={cardWidth}
      />
      <ScrollArea className="p-4">
        <div className="space-y-4">
          {navi && (
            <NaviOrderPanel
              vhost={navi.vhost}
              driverJid={navi.driverJid}
              driverPhone={navi.driverPhone}
              driverFirstName={navi.driverFirstName}
              driverLastName={navi.driverLastName}
              truckPlate={navi.truckPlate}
              trailerPlate={navi.trailerPlate}
              dispatcherFirstName={navi.dispatcherFirstName}
              dispatcherLastName={navi.dispatcherLastName}
              customerName={orderData?.customer_name}
              customerNip={orderData?.nip}
              recipientName={orderData?.recipient_name}
              recipientNip={orderData?.recipient_nip}
              onChange={({ complete, selection }) => {
                setNaviComplete(complete)
                setNaviSelectionState(selection)
              }}
            />
          )}
          {naviError && (
            <div className="px-1 text-sm text-red-600">{naviError}</div>
          )}
          {stops.map((stop, index) => {
            if (editingIndex === index) {
              return renderEditCard(stop, index)
            }
            return (
              <DraggableStopCard
                key={index}
                index={index}
                stop={stop}
                editingIndex={editingIndex}
                isEditingNotes={isEditingNotes}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMove={moveStop}
                getStepTitle={getStepTitle}
                formatDate={formatDate}
                measureRef={index === 0 ? cardMeasureRef : undefined}
                isFetchingPolylines={isFetchingPolylines}
                onCoordinatesChange={handleCoordinatesChange}
              />
            )
          })}

          {/* Add new stop button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddStop}
              disabled={editingIndex !== null || isFetchingPolylines}
            >
              {t("orders.verifyStops.addNewStop")}
            </Button>
          </div>

          {/* Order details section */}
          {showOrderDetails ? (
            <Card
              className={`group shadow-sm ${editingIndex !== null ? "opacity-50" : ""}`}
            >
              <CardHeader className="flex min-h-[58px] flex-row items-center justify-between p-4 pb-0 pt-2">
                <h3 className="text-lg font-semibold">
                  {t("orders.verifyStops.order.title")}
                </h3>
                {!isEditingNotes ? (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${editingIndex !== null ? "invisible" : "invisible group-hover:visible"}`}
                      onClick={handleEditNotes}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 text-red-500 hover:text-red-700 ${editingIndex !== null ? "invisible" : "invisible group-hover:visible"}`}
                      onClick={handleRemoveOrderDetails}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelNotes}
                      className="h-6"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSaveNotes(false)}
                      className="h-6 bg-blue-600 hover:bg-blue-700 dark:text-white"
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4 p-4 pt-0">
                {!isEditingNotes ? (
                  <>
                    {orderNumber ? (
                      <div
                        className={cn(
                          "flex items-center gap-3",
                          !isFetchingPolylines &&
                            "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                          isFetchingPolylines &&
                            "cursor-not-allowed opacity-50",
                        )}
                        onClick={
                          !isFetchingPolylines
                            ? () => handleEditOrderField("order_number")
                            : undefined
                        }
                      >
                        <Label className="text-sm font-medium">
                          {t("orders.verifyStops.order.orderNumberLabel")}
                        </Label>
                        <p className="select-text text-sm">{orderNumber}</p>
                      </div>
                    ) : (
                      <div>
                        <span
                          onClick={
                            !isFetchingPolylines
                              ? () => handleEditOrderField("order_number")
                              : undefined
                          }
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300",
                            !isFetchingPolylines && "cursor-text",
                            isFetchingPolylines &&
                              "cursor-not-allowed opacity-50",
                          )}
                        >
                          {t("orders.verifyStops.order.addOrderNumber")}
                        </span>
                      </div>
                    )}
                    {orderData.weight ? (
                      <div
                        className={cn(
                          "flex items-center gap-3",
                          !isFetchingPolylines &&
                            "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                          isFetchingPolylines &&
                            "cursor-not-allowed opacity-50",
                        )}
                        onClick={
                          !isFetchingPolylines
                            ? () => handleEditOrderField("weight")
                            : undefined
                        }
                      >
                        <Label className="text-sm font-medium">
                          {t("orders.verifyStops.order.weightLabel")}
                        </Label>
                        <p className="select-text text-sm">
                          {orderData.weight}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <span
                          onClick={
                            !isFetchingPolylines
                              ? () => handleEditOrderField("weight")
                              : undefined
                          }
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300",
                            !isFetchingPolylines && "cursor-text",
                            isFetchingPolylines &&
                              "cursor-not-allowed opacity-50",
                          )}
                        >
                          {t("orders.verifyStops.order.addWeight")}
                        </span>
                      </div>
                    )}
                    {/*{orderData.price_netto_eur !== undefined &&*/}
                    {/*orderData.price_netto_eur !== null ? (*/}
                    {/*  <div*/}
                    {/*    className={cn(*/}
                    {/*      "flex items-center gap-3",*/}
                    {/*      !isFetchingPolylines &&*/}
                    {/*        "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",*/}
                    {/*      isFetchingPolylines &&*/}
                    {/*        "cursor-not-allowed opacity-50",*/}
                    {/*    )}*/}
                    {/*    onClick={*/}
                    {/*      !isFetchingPolylines*/}
                    {/*        ? () => handleEditOrderField("price_netto_eur")*/}
                    {/*        : undefined*/}
                    {/*    }*/}
                    {/*  >*/}
                    {/*    <Label className="text-sm font-medium">*/}
                    {/*      Price (EUR):*/}
                    {/*    </Label>*/}
                    {/*    <p className="select-text text-sm">*/}
                    {/*      {orderData.price_netto_eur}*/}
                    {/*    </p>*/}
                    {/*  </div>*/}
                    {/*) : (*/}
                    {/*  <div>*/}
                    {/*    <span*/}
                    {/*      onClick={*/}
                    {/*        !isFetchingPolylines*/}
                    {/*          ? () => handleEditOrderField("price_netto_eur")*/}
                    {/*          : undefined*/}
                    {/*      }*/}
                    {/*      className={cn(*/}
                    {/*        "inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300",*/}
                    {/*        !isFetchingPolylines && "cursor-text",*/}
                    {/*        isFetchingPolylines &&*/}
                    {/*          "cursor-not-allowed opacity-50",*/}
                    {/*      )}*/}
                    {/*    >*/}
                    {/*      Add price*/}
                    {/*    </span>*/}
                    {/*  </div>*/}
                    {/*)}*/}
                    {orderData.notes ? (
                      <div
                        className={cn(
                          !isFetchingPolylines &&
                            "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                          isFetchingPolylines &&
                            "cursor-not-allowed opacity-50",
                        )}
                        onClick={
                          !isFetchingPolylines
                            ? () => handleEditOrderField("notes")
                            : undefined
                        }
                      >
                        <p className="select-text text-sm">{orderData.notes}</p>
                      </div>
                    ) : (
                      <div>
                        <span
                          onClick={
                            !isFetchingPolylines
                              ? () => handleEditOrderField("notes")
                              : undefined
                          }
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300",
                            !isFetchingPolylines && "cursor-text",
                            isFetchingPolylines &&
                              "cursor-not-allowed opacity-50",
                          )}
                        >
                          {t("orders.verifyStops.order.addNotes")}
                        </span>
                      </div>
                    )}
                    {orderData.adr_info ? (
                      <div
                        className={cn(
                          "flex items-center gap-3",
                          !isFetchingPolylines &&
                            "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                          isFetchingPolylines &&
                            "cursor-not-allowed opacity-50",
                        )}
                        onClick={
                          !isFetchingPolylines
                            ? () => handleEditOrderField("adr_info")
                            : undefined
                        }
                      >
                        <Label className="text-sm font-medium">
                          {t("orders.verifyStops.order.adrLabel")}
                        </Label>
                        <p className="select-text text-sm">
                          {orderData.adr_info}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <span
                          onClick={
                            !isFetchingPolylines
                              ? () => handleEditOrderField("adr_info")
                              : undefined
                          }
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-sm text-amber-700 dark:bg-amber-900 dark:text-amber-300",
                            !isFetchingPolylines && "cursor-text",
                            isFetchingPolylines &&
                              "cursor-not-allowed opacity-50",
                          )}
                        >
                          {t("orders.verifyStops.order.addAdr")}
                        </span>
                      </div>
                    )}
                    <div
                      className={cn(
                        "flex items-center gap-3",
                        !isFetchingPolylines &&
                          "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                        isFetchingPolylines && "cursor-not-allowed opacity-50",
                      )}
                      onClick={
                        !isFetchingPolylines
                          ? () => handleEditOrderField("pallet_exchange")
                          : undefined
                      }
                      data-testid="order-pallet-exchange"
                    >
                      <Label className="text-sm font-medium">
                        {t("orders.verifyStops.order.palletExchangeLabel")}
                      </Label>
                      <p className="select-text text-sm">
                        {formatPalletExchangeValue(orderData.pallet_exchange) ??
                          "—"}
                      </p>
                    </div>
                    <div
                      className={cn(
                        "flex items-center gap-3",
                        !isFetchingPolylines &&
                          "cursor-text hover:bg-gray-50 dark:hover:bg-gray-800",
                        isFetchingPolylines && "cursor-not-allowed opacity-50",
                      )}
                      onClick={
                        !isFetchingPolylines
                          ? () => handleEditOrderField("temperature_min")
                          : undefined
                      }
                      data-testid="order-temperature"
                    >
                      <Label className="text-sm font-medium">
                        {t("orders.verifyStops.order.temperatureLabel")}
                      </Label>
                      <p className="select-text text-sm">
                        {formatTemperatureRange(
                          orderData.temperature_min,
                          orderData.temperature_max,
                        ) ?? "—"}
                      </p>
                    </div>
                    <BillingDetailsSection
                      billing={orderData.billing}
                      priceNettoEur={orderData.price_netto_eur ?? undefined}
                    />
                  </>
                ) : (
                  <>
                    <div>
                      <Label
                        htmlFor="order_number"
                        className="text-sm font-medium"
                      >
                        {t("orders.verifyStops.order.orderNumber")}
                      </Label>
                      <Input
                        id="order_number"
                        value={orderNumber}
                        onChange={(e) =>
                          setOrderData((prev) => ({
                            ...prev,
                            order_number: e.target.value,
                          }))
                        }
                        className="mt-1"
                        autoFocus={focusField === "order_number"}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label htmlFor="weight" className="text-sm font-medium">
                        {t("orders.verifyStops.order.weight")}
                      </Label>
                      <Input
                        id="weight"
                        type="text"
                        value={orderData.weight || ""}
                        onChange={(e) =>
                          setOrderData((prev) => ({
                            ...prev,
                            weight: e.target.value,
                          }))
                        }
                        className="mt-1"
                        autoFocus={focusField === "weight"}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="price_netto_eur"
                        className="text-sm font-medium"
                      >
                        {t("orders.verifyStops.order.price")}
                      </Label>
                      <Input
                        id="price_netto_eur"
                        type="number"
                        step="0.01"
                        value={orderData.price_netto_eur ?? ""}
                        onChange={(e) =>
                          setOrderData((prev) => ({
                            ...prev,
                            price_netto_eur: e.target.value
                              ? parseFloat(e.target.value)
                              : undefined,
                          }))
                        }
                        className="mt-1"
                        autoFocus={focusField === "price_netto_eur"}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-sm font-medium">
                        {t("orders.verifyStops.order.notes")}
                      </Label>
                      <Textarea
                        id="notes"
                        value={orderData.notes || ""}
                        onChange={(e) =>
                          setOrderData((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        className="mt-1"
                        rows={4}
                        placeholder={t(
                          "orders.verifyStops.order.notesPlaceholder",
                        )}
                        autoFocus={focusField === "notes"}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label htmlFor="adr_info" className="text-sm font-medium">
                        {t("orders.verifyStops.order.adr")}
                      </Label>
                      <Textarea
                        id="adr_info"
                        value={orderData.adr_info || ""}
                        onChange={(e) =>
                          setOrderData((prev) => ({
                            ...prev,
                            adr_info: e.target.value,
                          }))
                        }
                        className="mt-1"
                        rows={2}
                        placeholder={t(
                          "orders.verifyStops.order.adrPlaceholder",
                        )}
                        autoFocus={focusField === "adr_info"}
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="pallet_exchange"
                        className="text-sm font-medium"
                      >
                        {t("orders.verifyStops.order.palletExchange")}
                      </Label>
                      <Select
                        value={
                          orderData.pallet_exchange === true
                            ? "true"
                            : orderData.pallet_exchange === false
                              ? "false"
                              : "unset"
                        }
                        onValueChange={(value) =>
                          setOrderData((prev) => ({
                            ...prev,
                            pallet_exchange:
                              value === "true"
                                ? true
                                : value === "false"
                                  ? false
                                  : undefined,
                          }))
                        }
                      >
                        <SelectTrigger
                          id="pallet_exchange"
                          className="mt-1"
                          autoFocus={focusField === "pallet_exchange"}
                        >
                          <SelectValue
                            placeholder={t("orders.verifyStops.order.noData")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unset">
                            {t("orders.verifyStops.order.noData")}
                          </SelectItem>
                          <SelectItem value="true">
                            {t("orders.verifyStops.order.yes")}
                          </SelectItem>
                          <SelectItem value="false">
                            {t("orders.verifyStops.order.no")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label
                        htmlFor="temperature_min"
                        className="text-sm font-medium"
                      >
                        {t("orders.verifyStops.order.temperature")}
                      </Label>
                      <div className="mt-1 flex items-center gap-2">
                        <Input
                          id="temperature_min"
                          type="number"
                          step="0.5"
                          placeholder={t(
                            "orders.verifyStops.order.temperatureFrom",
                          )}
                          value={orderData.temperature_min ?? ""}
                          onChange={(e) =>
                            setOrderData((prev) => ({
                              ...prev,
                              temperature_min: parseTemperatureInput(
                                e.target.value,
                              ),
                            }))
                          }
                          autoFocus={focusField === "temperature_min"}
                          onFocus={(e) => e.target.select()}
                        />
                        <span className="text-sm text-muted-foreground">–</span>
                        <Input
                          id="temperature_max"
                          type="number"
                          step="0.5"
                          placeholder={t(
                            "orders.verifyStops.order.temperatureTo",
                          )}
                          value={orderData.temperature_max ?? ""}
                          onChange={(e) =>
                            setOrderData((prev) => ({
                              ...prev,
                              temperature_max: parseTemperatureInput(
                                e.target.value,
                              ),
                            }))
                          }
                          autoFocus={focusField === "temperature_max"}
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                    </div>
                    <BillingDetailsSection
                      billing={orderData.billing}
                      priceNettoEur={orderData.price_netto_eur ?? undefined}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Add Order Details button when no order details exist */
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddOrderDetails}
                disabled={editingIndex !== null || isFetchingPolylines}
              >
                {t("orders.verifyStops.order.addDetails")}
              </Button>
            </div>
          )}
          {/* Continue button */}
        </div>
      </ScrollArea>
      <div
        className={`flex flex-1 items-end ${offerSkipStep ? "justify-between" : "justify-end"} gap-2 p-4`}
      >
        {inProduct && (
          <Button variant="ghost" onClick={onCloseFullScreen}>
            {t("common.close")}
          </Button>
        )}
        {offerSkipStep && (
          <div className="flex h-9 flex-1 items-center justify-end gap-1">
            <Switch
              id="verify"
              checked={shouldSkipVerifyStep}
              onCheckedChange={(checked) => setShouldSkipVerifyStep(checked)}
            />
            <Label className="text-xs text-muted-foreground" htmlFor="verify">
              {t("orders.verifyStops.skipNextTime")}
            </Label>
          </div>
        )}

        <Button
          disabled={
            isFetchingPolylines ||
            stops?.length === 0 ||
            naviCreating ||
            (!!navi && !naviComplete) ||
            // The Marcos order is built from the previewed points, so it must
            // not go out while gps still owes one for some stop.
            (!!navi && !!client && geocodeInFlight(stops))
          }
          className="bg-blue-600 px-3 hover:bg-blue-700 dark:text-white"
          onClick={async () => {
            // Save current editing state before continuing
            if (editingIndex !== null) {
              handleSave(true)
              return
            }
            if (isEditingNotes) {
              handleSaveNotes(true)
              return
            }
            // When Marcos is configured, create the order FIRST; only proceed
            // to route generation if it returns a transport id (HTTP 200).
            if (navi && client) {
              if (geocodeInFlight(stops)) return
              setNaviError(undefined)
              setNaviCreating(true)
              // Raise the full-panel overlay; it stays up through order
              // creation and route generation until we move to the map.
              navi.onSubmitStart?.()
              try {
                const price = orderData?.price_netto_eur
                const enrichedSelection = {
                  ...(naviSelection ?? {}),
                  // Freight only — the extracted price is what the customer
                  // pays us (revenue). Do NOT send it as carrierCharge: Marcos
                  // auto-creates a cost position from CarrierCharge, booking
                  // the freight as a carrier cost.
                  ...(price && price > 0
                    ? {
                        freight: price,
                        freightCurrency: "EUR",
                      }
                    : {}),
                  ...(orderData?.order_number
                    ? { customerReference: orderData.order_number }
                    : {}),
                  // Order-level reference; lands in each connection's Remarks
                  // ("Ref number: ...") on the Marcos side.
                  ...(orderData?.ref_number
                    ? { refNumber: orderData.ref_number }
                    : {}),
                  // Many documents print only one number total, so
                  // extraction assigns it to order_number/ref_number/
                  // transport_number inconsistently between runs. Send this
                  // one too — the backend falls back to it for
                  // customerReference when neither of the above is set.
                  ...(orderData?.transport_number
                    ? { transportNumber: orderData.transport_number }
                    : {}),
                  ...(deriveIsAdr(orderData) ? { isAdr: true } : {}),
                  ...(orderData?.billing?.payment_terms_days
                    ? {
                        paymentTermsDays: orderData.billing.payment_terms_days,
                      }
                    : {}),
                }
                const res = await AdHocCommands.createNaviOrder(
                  client,
                  navi.vhost,
                  navi.driverJid,
                  enrichedSelection,
                  withGeocodedCoordinates(stops),
                  firstUploadedFile?.url,
                  firstUploadedFile?.name,
                )
                const parsed = parseNaviJson(res)
                if (!parsed?.transport_id) {
                  setNaviError(
                    parsed?.detail ||
                      t("orders.verifyStops.marcos.createFailed"),
                  )
                  navi.onSubmitError?.()
                  return
                }
                // Partial success: the order was created in Marcos but the sync
                // step (filling all fields) failed, so it landed in "imported".
                // Warn the dispatcher but continue — the order does exist.
                if (parsed.sync_failed) {
                  const syncMsg =
                    parsed.sync_failed_reason === "address_not_found"
                      ? t("orders.verifyStops.marcos.addressNotFound")
                      : t("orders.verifyStops.marcos.syncFailed")
                  window.appToaster.show({
                    message: syncMsg,
                    intent: "warning",
                    // timeout 0 = stays until the dispatcher dismisses it.
                    timeout: 0,
                  })
                }
                if (parsed.attachment_failed) {
                  window.appToaster.show({
                    message: t("orders.verifyStops.marcos.attachmentFailed"),
                    intent: "warning",
                    timeout: 0,
                  })
                }
                navi.setNaviTransportId(parsed.transport_id)
                navi.setNaviSelection(naviSelection)
              } catch {
                setNaviError(t("orders.verifyStops.marcos.connectionError"))
                navi.onSubmitError?.()
                return
              } finally {
                setNaviCreating(false)
              }
            }
            handleContinueWithTracking({ ...orderData, stops })
          }}
        >
          {isFetchingPolylines ? (
            <>
              <Spinner size="sm" />
              {t("orders.verifyStops.generatingRoute")}
            </>
          ) : continueLabel ? (
            continueLabel
          ) : editingIndex !== null || isEditingNotes ? (
            t("orders.verifyStops.saveAndGenerateRoute")
          ) : isRouteFetched ? (
            t("orders.verifyStops.continue")
          ) : (
            t("orders.verifyStops.generateRoute")
          )}
        </Button>
      </div>
    </>
  )
}
